import * as React from 'react';
// import hooks
import {
	UseInputFormResult,
	UseInputTextResults,
	useInputText,
} from '@app/hooks/view/input';
// import props
import { 
	FormPopupMinCostPerSquareFootVersionProps,
	FormPopupProps,
	NavigationFooterProps,
 } from '@app/props';
 // import enums
 import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
 } from '@app/enumerations';
 
// import api models
import { MinCostPerSquareFootVersionRequest } from '@app/models/api';
import { Check, Close } from '@mui/icons-material';

interface UseFormPopupMinCostPerSquareFootVersionParameters {
	id: string;
	props: FormPopupMinCostPerSquareFootVersionProps;
}

interface UseFormPopupMinCostPerSquareFootVersionResults {
	descriptionInput: UseInputTextResults;
	applyFormProps: () => FormPopupProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupMinCostPerSquareFootVersion = (
	params: UseFormPopupMinCostPerSquareFootVersionParameters
): UseFormPopupMinCostPerSquareFootVersionResults => {
	//#region exported methods
	const applyFormProps = (): FormPopupProps => {
		return {
			fixedNavigation: true,
			id: generateIdForForm(),
			isOpen: params.props.isOpen,
			onCloseClick: params.props.onClose,
			popupWidth: PopupWidth.MEDIUM,
			themeType: AppThemeColor.PRIMARY,
			title: params.props.popupTitle,
		};
	};
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: onSave,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};
	//#endregion exported methods

	//#region internal methods
	const checkInputValidationWithFocus = <T>(
		input: UseInputFormResult<T>
	): boolean => {
		const isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		
		}
		return isValid;
	};

	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<string>(descriptionInput);
	};

	const flagAllInputsToShowValidations = (): void => {
		descriptionInput.changeShowValidationFlag(true);
	}

	const onSave = (): void => {
		flagAllInputsToShowValidations();
		const isFormValid = checkFormValidationWithFocus();
		if (isFormValid) {
			const request: MinCostPerSquareFootVersionRequest = {
				description: descriptionInput.value,
			};
			params.props.onSave(request);
		};
	};
	const generateIdForForm = (): string => {
		return params.id.toString();
	};
	
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigation-footer`;
	};

	const generateIdForDescriptionInput = (): string => {
		return `${generateIdForForm()}-description-input`;
	};
	//#endregion internal methods

	//#region inputs
	const descriptionInput : UseInputTextResults = useInputText({
		id: generateIdForDescriptionInput(),
		required: true,
		disabled: false,
		infoText: 'Enter a description for this version.',
		label: 'Description',
		value: params.props.minCostPerSquareFootVersion?.description ?? String.empty(),
	});
	//#endregion inputs

	//#region return
	return {
		descriptionInput,
		applyFormProps,
		applyNavigationFooterProps,
	};
	//#endregion return
};

//export parameters
export { UseFormPopupMinCostPerSquareFootVersionParameters};

//export results
export type {
	UseFormPopupMinCostPerSquareFootVersionResults,
};

export default useFormPopupMinCostPerSquareFootVersion;